import React, { useEffect, useMemo, useState } from "react";

import { Link, useLocation } from "react-router-dom";

import { latestSuccessAttempt } from "../lib/after-auth";
import {
  DatabaseCollectionDto,
  DatabaseRelationDto,
} from "../lib/generated/api-client";
import { getApi } from "../lib/get-api";
import {
  getActivePreviewMemberId,
  hrefWithPreview,
  previewCacheToken,
  previewIdFromLocation,
} from "../lib/preview-as";
import { humanize, singularize } from "../lib/schema-field";
import { useAuth } from "../lib/useAuth";
import { useDatasources } from "../lib/useDatasources";

const LIST_CAP = 8;

type Props = {
  collection?: DatabaseCollectionDto;
  model?: any;
  demoMode?: boolean;
};

type ResolvedIncoming = {
  relation: DatabaseRelationDto;
  child: DatabaseCollectionDto;
  fkName: string;
  parentKeyName: string;
  parentKeyValue: string | number | boolean;
};

type RelationState = {
  loading: boolean;
  error: string | null;
  items: any[];
  total: number;
};

const rowLabel = (row: any): string => {
  if (!row || typeof row !== "object") return "Record";
  for (const key of ["name", "title", "label", "slug"] as const) {
    const candidate = row[key];
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  if (row.id != null && row.id !== "") return `#${row.id}`;
  return "Record";
};

const primaryName = (collection?: DatabaseCollectionDto): string =>
  collection?.fields?.find((field) => field.primary)?.name ?? "id";

const resolveIncoming = (
  collection: DatabaseCollectionDto | undefined,
  model: any,
  collections: DatabaseCollectionDto[]
): ResolvedIncoming[] => {
  const incoming = collection?.targetRelations;
  if (!collection || !incoming?.length) return [];

  const resolved: ResolvedIncoming[] = [];
  for (const relation of incoming) {
    const child = collections.find((c) => c.id === relation.databaseCollectionId);
    if (!child?.name) continue;

    const fkName =
      child.fields?.find((f) => f.id === relation.databaseFieldId)?.name ??
      relation.fields?.[0];
    if (!fkName) continue;

    const parentKeyName =
      child.fields?.find((f) => f.id === relation.targetFieldId)?.name ??
      collection.fields?.find((f) => f.id === relation.targetFieldId)?.name ??
      primaryName(collection);

    const parentKeyValue = model?.[parentKeyName];
    if (parentKeyValue == null || parentKeyValue === "") continue;

    resolved.push({
      relation,
      child,
      fkName,
      parentKeyName,
      parentKeyValue,
    });
  }
  return resolved;
};

const filterGroupsJson = (fkName: string, parentKeyValue: unknown): string =>
  JSON.stringify({
    operator: "AND",
    filters: [{ field: fkName, operator: "==", value: parentKeyValue }],
  });

const tableTo = (
  datasourceId: string,
  childName: string,
  filterGroups: string,
  extraQuery = ""
) =>
  hrefWithPreview(
    `/datasources/${datasourceId}/collections/${encodeURIComponent(childName)}?filterGroups=${encodeURIComponent(filterGroups)}${extraQuery}`,
    getActivePreviewMemberId(),
  );

const IncomingRelation: React.FC<{
  incoming: ResolvedIncoming;
  datasourceId: string;
  demoMode?: boolean;
}> = ({ incoming, datasourceId, demoMode }) => {
  const { token } = useAuth();
  const location = useLocation();
  const previewToken = previewCacheToken(previewIdFromLocation(location));
  const [state, setState] = useState<RelationState>({
    loading: true,
    error: null,
    items: [],
    total: 0,
  });

  const filterGroups = useMemo(
    () => filterGroupsJson(incoming.fkName, incoming.parentKeyValue),
    [incoming.fkName, incoming.parentKeyValue]
  );

  useEffect(() => {
    let cancelled = false;
    const api = getApi({ token });

    const load = async () => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await api.data.listData(datasourceId, incoming.child.name, {
          filterGroups,
          limit: LIST_CAP,
        });
        if (cancelled) return;
        const data = response.data;
        const items = Array.isArray(data?.items) ? data.items : [];
        setState({
          loading: false,
          error: null,
          items,
          total: typeof data?.total === "number" ? data.total : items.length,
        });
      } catch (err: any) {
        if (cancelled) return;
        const apiMessage = err?.response?.data?.message ?? err?.message;
        setState({
          loading: false,
          error:
            typeof apiMessage === "string"
              ? apiMessage
              : "Could not load related records.",
          items: [],
          total: 0,
        });
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [datasourceId, incoming.child.name, filterGroups, token, previewToken]);

  const extra = Math.max(0, state.total - state.items.length);
  const countLabel = state.loading ? "…" : String(state.total);
  const childPkName = primaryName(incoming.child);
  const filteredTable = tableTo(
    datasourceId,
    incoming.child.name,
    filterGroups
  );
  const addTo = tableTo(
    datasourceId,
    incoming.child.name,
    filterGroups,
    `&new=1&defaults=${encodeURIComponent(
      JSON.stringify({ [incoming.fkName]: incoming.parentKeyValue })
    )}`
  );
  const addLabel = `Add ${singularize(incoming.child.name)}`;

  return (
    <div className="rounded-lg border border-od-border bg-od-surface-2 px-2.5 py-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="m-0 text-xs font-semibold text-od-text">
          {humanize(incoming.child.name)}
          <span className="ml-1.5 font-normal text-od-muted">{countLabel}</span>
        </h4>
        <div className="flex flex-wrap items-baseline gap-2.5">
          {!demoMode && (
            <Link
              to={addTo}
              className="inline-flex items-center text-xs font-medium text-od-primary-text transition-colors hover:text-od-accent-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-od-primary-text [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
            >
              {addLabel}
            </Link>
          )}
          <Link
            to={filteredTable}
            className="inline-flex items-center text-xs font-medium text-od-primary-text transition-colors hover:text-od-accent-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-od-primary-text [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
          >
            Open in table
          </Link>
        </div>
      </div>

      {state.loading && (
        <p className="mb-0 mt-1.5 text-xs text-od-muted">Loading related rows…</p>
      )}

      {!state.loading && state.error && (
        <p className="mb-0 mt-1.5 text-xs text-od-danger">{state.error}</p>
      )}

      {!state.loading && !state.error && state.items.length === 0 && (
        <p className="mb-0 mt-1.5 text-xs text-od-muted">No related records.</p>
      )}

      {!state.loading && !state.error && state.items.length > 0 && (
        <ul className="mb-0 mt-1.5 list-none space-y-0.5 p-0">
          {state.items.map((row, index) => {
            const pk = row?.[childPkName];
            const label = rowLabel(row);
            const rowTo = tableTo(
              datasourceId,
              incoming.child.name,
              filterGroups,
              `&open=${encodeURIComponent(String(pk ?? ""))}`
            );
            return (
              <li
                key={pk != null && pk !== "" ? String(pk) : index}
                className="truncate text-xs"
                title={label}
              >
                <Link
                  to={rowTo}
                  className="inline-flex max-w-full items-center text-od-primary-text transition-colors hover:text-od-accent-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-od-primary-text focus-visible:-outline-offset-2 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
                >
                  <span className="truncate">{label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {!state.loading && extra > 0 && (
        <p className="mb-0 mt-1 text-xs text-od-muted">+{extra} more</p>
      )}
    </div>
  );
};

/**
 * Incoming FKs (`collection.targetRelations`) on the edit drawer: child tables
 * that point at this row, a short list of matching records, and a hash link
 * into that table already filtered to them.
 */
export const RelatedRecords: React.FC<Props> = ({
  collection,
  model,
  demoMode,
}) => {
  const { datasources } = useDatasources();
  const incoming = useMemo(
    () => {
      const collections =
        latestSuccessAttempt(
          datasources.find((ds) => ds.id === collection?.datasourceId)?.analyzeAttempts
        )?.databaseCollections ?? [];
      return resolveIncoming(collection, model, collections);
    },
    [collection, model, datasources]
  );

  if (!incoming.length || !collection?.datasourceId) return null;

  return (
    <section className="flex flex-col gap-2 border-t border-od-border pt-3">
      <h3 className="m-0 text-xs font-medium uppercase tracking-wide text-od-muted">
        Related records
      </h3>
      {incoming.map((item) => (
        <IncomingRelation
          key={item.relation.id}
          incoming={item}
          datasourceId={collection.datasourceId}
          demoMode={demoMode}
        />
      ))}
    </section>
  );
};
